import { applicationApi } from "../../common/api";
import { Admin, AdminPosition, AdminPositionCode, KPIBaseEntity, KPIExecutionEntity } from './types';

// Define a service using a base URL and expected endpoints
export const adminApi = applicationApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllAdmin: builder.query<Admin[], void>({
      query: () => `admins`,
      providesTags: [{ type: 'Admin', id: 'LIST' }],
    }),
    getAdmin: builder.query<Admin, string>({
      query: (query) => `admins/${query}`,
      providesTags: [{ type: 'Admin', id: 'LIST' }],
    }),
    createAdmin: builder.mutation<any, any>({
      query: (body) => ({
        url: '/admins',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Admin', id: 'LIST' }],
    }),
    deleteAdmin: builder.mutation<any, number>({
      query: (id) => ({
        url: `admins/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Admin', id: 'LIST' }],
    }),
    updateAdmin: builder.mutation<Admin, any>({
      query: (body) => ({
        url: `/admins/${body.id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [{ type: 'Admin', id: 'LIST' }],
    }),
    getAllAdminPosition: builder.query<AdminPosition[], void>({
      query: () => `admin-positions`,
      providesTags: [{ type: 'AdminPosition', id: 'LIST' }],
    }),
    getAdminPositionCode: builder.query<AdminPositionCode, void>({
      query: () => `admin-positions/code`,
      providesTags: [{ type: 'AdminPositionCode', id: 'LIST' }],
    }),
    getAdminPosition: builder.query<AdminPosition, string>({
      query: (query) => `admin-positions/${query}`,
      providesTags: [{ type: 'AdminPosition', id: 'LIST' }],
    }),
    createAdminPosition: builder.mutation<any, any>({
      query: (body) => ({
        url: `/admin-positions`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'AdminPosition', id: 'LIST' }],
    }),
    deleteAdminPosition: builder.mutation<any, number>({
      query: (id) => ({
        url: `admin-positions/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'AdminPosition', id: 'LIST' }],
    }),
    updateAdminPosition: builder.mutation<AdminPosition, any>({
      query: (body) => ({
        url: `/admin-positions/${body.id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [{ type: 'AdminPosition', id: 'LIST' }],
    }),
    getKpiList: builder.query<KPIBaseEntity[], void>({
      query: (query) => `kpi/manage`,
      providesTags: [{ type: 'KPI', id: 'LIST' }],
    }),
    getKpi: builder.query<KPIBaseEntity, string>({
      query: (query) => `kpi/manage/${query}`,
      providesTags: [{ type: 'KPI', id: 'LIST' }],
    }),
    createKpi: builder.mutation<any, any>({
      query: (body) => ({
        url: `kpi/manage/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'KPI', id: 'LIST' }],
    }),
    deleteKpi: builder.mutation<any, number>({
      query: (id) => ({
        url: `kpi/manage/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'KPI', id: 'LIST' }],
    }),
    updateKpi: builder.mutation<KPIBaseEntity, any>({
      query: (body) => ({
        url: `/kpi/manage/${body.id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [{ type: 'KPI', id: 'LIST' }],
    }),
    getKpiExecution: builder.query<KPIExecutionEntity[], any>({
      query: (params) => ({
        url: '/kpi/execution',
        method: 'GET',
        params: params,
      }),
      providesTags: [{ type: 'KPIExecution', id: 'LIST' }, { type: 'KPIExecution' }],
    }),
    generateKpiExecution: builder.mutation<any, any>({
      query: (params) => ({
        url: '/kpi/execution/generate',
        method: 'POST',
        params: params,
      }),
      invalidatesTags: [{ type: 'KPIExecution', id: 'LIST' }],
    }),
    saveKpiExecutionItem: builder.mutation<void, any>({
      query: (body) => ({
        url: '/kpi/execution/save-item',
        method: 'POST',
        body: body,
      }),
      invalidatesTags: [{ type: 'KPIExecution', id: 'LIST' }],
    }),
    getKpiExecutionManager: builder.query<KPIExecutionEntity[], any>({
      query: (params) => ({
        url: '/kpi/manage/execution',
        method: 'GET',
        params: params,
      }),
      providesTags: [{ type: 'KPIExecution', id: 'LIST' }, { type: 'KPIExecution' }],
    }),
    deleteExecutionManager: builder.mutation<any, any>({
      query: (params) => ({
        url: '/kpi/manage/execution',
        method: 'DELETE',
        params: params,
      }),
      invalidatesTags: [{ type: 'KPIExecution', id: 'LIST' }],
    }),
    approveExecutionManager: builder.mutation<any, any>({
      query: (params) => ({
        url: '/kpi/manage/execution/approve',
        method: 'POST',
        params: params,
      }),
      invalidatesTags: [{ type: 'KPIExecution', id: 'LIST' }],
    }),
    saveKpiExecutionItemManager: builder.mutation<void, any>({
      query: (data) => ({
        url: `/kpi/manage/execution/save-item/${data.params}`,
        method: 'POST',
        body: data.body,
      }),
      invalidatesTags: [{ type: 'KPIExecution', id: 'LIST' }],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetAllAdminQuery,
  useGetAdminQuery,
  useCreateAdminMutation,
  useDeleteAdminMutation,
  useUpdateAdminMutation,
  useGetAllAdminPositionQuery,
  useGetAdminPositionCodeQuery,
  useGetAdminPositionQuery,
  useCreateAdminPositionMutation,
  useUpdateAdminPositionMutation,
  useDeleteAdminPositionMutation,
  useGetKpiListQuery,
  useGetKpiQuery,
  useCreateKpiMutation,
  useUpdateKpiMutation,
  useDeleteKpiMutation,
  useGetKpiExecutionQuery,
  useGenerateKpiExecutionMutation,
  useSaveKpiExecutionItemMutation,
  useGetKpiExecutionManagerQuery,
  useDeleteExecutionManagerMutation,
  useApproveExecutionManagerMutation,
  useSaveKpiExecutionItemManagerMutation,
} = adminApi;
